import React from 'react';

import { Link } from '~/lib/react-router-dom';

import { WarningAmber } from '@mui/icons-material';
import { Box, Chip, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import CopyableField from '~/components/Common/CopyableField';
import DebugModal from '~/components/DebugModal/DebugModal';
import caseIdRenderer from '~/components/Promo/PromoPage/caseIdRenderer';
import { promoCodePath, promoIdPath } from '~/components/Promo/promoUrls';

import { PromoMeta } from '~/services/PromoService';
import { formatDateWeekDayShortMonth, isBefore } from '~/services/TimeService';

import { PromoToggleFormatter } from '../formatters/PromoToggleFormatter';

import PromoDetails from './PromoDetails';
import actionsRenderer from './actionsRenderer';
import descriptionRenderer from './descriptionRenderer';
import devicesRenderer from './devicesRenderer';
import limitsRenderer from './limitsRenderer';
import popupMessageRenderer from './promoPopupMessage';

const getColumns = (meta: PromoMeta): Array<GridColDef<App.PromoData>> => [
  {
    field: 'code_name',
    headerName: 'Information',
    sortable: false,
    flex: 5,
    renderCell: descriptionRenderer,
    display: 'flex',
  },
  {
    field: 'default_display',
    headerName: 'Details',
    sortable: false,
    flex: 2,
    renderCell: (params: GridRenderCellParams<App.PromoData>) => <PromoDetails {...params} />,
    display: 'flex',
  },
  {
    field: 'device_types',
    headerName: 'Devices',
    sortable: false,
    renderCell: devicesRenderer,
    display: 'flex',
  },
  {
    field: 'case_id',
    headerName: 'Case ID',
    sortable: false,
    flex: 2,
    renderCell: caseIdRenderer,
    display: 'flex',
  },
  {
    field: 'code_limit',
    headerName: 'Limits',
    sortable: false,
    flex: 2,
    renderCell: limitsRenderer,
    display: 'flex',
  },
  {
    field: 'popup_message_presented_example',
    headerName: '(Example) Popup Message',
    sortable: false,
    flex: 2,
    renderCell: (params) => popupMessageRenderer(params.row.popup_message_presented_example),
    display: 'flex',
  },
  {
    field: 'promo_toggles',
    headerName: 'Toggles',
    sortable: false,
    renderCell: (params) => {
      if (params.row.promo_toggles === null || params.row.promo_toggles === undefined) {
        return null;
      }

      if (params.row.promo_toggles.length === 0) {
        return null;
      }

      if (!meta) {
        return (
          <Box title="Could not load promo meta">
            <WarningAmber />
          </Box>
        );
      }

      return PromoToggleFormatter({
        promoToggles: params.row.promo_toggles,
        promoToggleInfo: meta.promo_toggles,
        direction: 'column',
      });
    },
  },
  {
    field: 'active',
    headerName: 'Status',
    sortable: false,
    minWidth: 200,
    flex: 1,
    renderCell: actionsRenderer,
    display: 'flex',
  },
];

export const getRefinedColumns = (meta: PromoMeta): Array<GridColDef<App.PromoData>> => [
  {
    field: 'code_name',
    headerName: 'Promo code',
    flex: 2,
    minWidth: 240,
    renderCell: (params) => (
      <CopyableField
        value={params.row.code_name}
        label={
          <Link to={promoCodePath(params.row.code_name)} target="_blank" rel="noreferrer">
            {params.row.code_name}
          </Link>
        }
      />
    ),
  },
  {
    field: 'promo_discounts',
    headerName: 'Discount',
    flex: 1,
    minWidth: 150,
    sortable: false,
    renderCell: (params) => {
      const discounts = params.row.promo_discounts ?? [];
      if (discounts.length === 0) {
        return 'No discount configured';
      }

      const [discount] = discounts;
      const value =
        params.row.promo_type === 'percentage'
          ? `${discount.discount_value}%`
          : `${params.row.currency} ${discount.discount_value}`;
      const additionalDiscounts = discounts.length > 1 ? ` + ${discounts.length - 1} regional` : '';

      return (
        <Tooltip title={`${discounts.length} discount configuration${discounts.length === 1 ? '' : 's'}`}>
          <span>{`${value}${additionalDiscounts}`}</span>
        </Tooltip>
      );
    },
  },
  {
    field: 'usage_count',
    headerName: 'Usage',
    flex: 1,
    minWidth: 130,
    sortable: false,
    renderCell: (params) => {
      const usageCount = params.row.usage_count ?? 0;
      const codeLimit = params.row.code_limit;

      return codeLimit && codeLimit > 0 ? `${usageCount}/${codeLimit} used` : `${usageCount} used`;
    },
  },
  {
    field: 'added_by',
    headerName: 'Created by',
    flex: 1,
    minWidth: 170,
    sortable: false,
    renderCell: (params) => {
      const addedBy = params.row.addedBy;
      if (!params.row.added_by) {
        return null;
      }

      return (
        <Link to={`/users/${params.row.added_by}`} target="_blank" rel="noreferrer">
          {addedBy?.full_name ?? params.row.added_by}
        </Link>
      );
    },
  },
  {
    field: 'cloned_from_promo_id',
    headerName: 'Parent promo',
    flex: 1,
    minWidth: 180,
    renderCell: (params) =>
      params.row.cloned_from_promo_id ? (
        <Link to={promoIdPath(params.row.cloned_from_promo_id)} target="_blank" rel="noreferrer">
          {params.row.cloned_from_promo_id}
        </Link>
      ) : null,
  },
  {
    field: 'expires_at',
    headerName: 'Expiry date',
    flex: 1,
    minWidth: 150,
    renderCell: (params) => {
      const isExpired = isBefore(params.row.expires_at);
      return (
        <Chip
          label={formatDateWeekDayShortMonth(params.row.expires_at)}
          size="small"
          color={isExpired ? 'error' : 'success'}
          variant={isExpired ? 'filled' : 'outlined'}
        />
      );
    },
  },
  {
    field: 'promo_toggles',
    headerName: 'Toggles',
    flex: 1,
    minWidth: 150,
    renderCell: (params) => {
      const toggles = params.row.promo_toggles ?? [];
      const toggleNames = toggles
        .map((toggle) => meta.promo_toggles.find((option) => option.key === toggle)?.internalDescription ?? toggle)
        .join(', ');

      return (
        <Tooltip title={toggleNames}>
          <Chip label={`${toggles.length} configured`} size="small" variant="outlined" />
        </Tooltip>
      );
    },
  },
  {
    field: 'debug',
    headerName: 'Debug',
    minWidth: 100,
    sortable: false,
    renderCell: (params) => (
      <DebugModal title="Debug" type="generic" data={params.row} buttonProps={{ size: 'small', variant: 'text' }} />
    ),
  },
];

export default getColumns;
